// Block shape: whether each block of the hierarchy is ONE tidy region of the wall.
//
// Orientation-free on purpose. A block is one piece when its facings join up, along a shelf with
// nothing foreign between them or across neighbouring shelves where they overlap in x, and it is
// tidy when that piece fills its own bounding rectangle. A block met twice along one shelf is
// charged for it even when the shelves above and below stitch its halves into one piece: a comb
// of interleaved facings is connected, and it is still a mess. A clean vertical stripe and a clean
// horizontal band both pass; a staircase, an L, a stray facing or a scatter do not. Nothing here
// reads which way a level was meant to run, so no inference from the plan can flatter the plan.
import { PATH_SEPARATOR } from "./studioTypes";

export interface ShapeStretch {
  run: number;
  shelf: number;
  x: number;
  width: number;
  packWidth: number;
  attrs: Readonly<Record<string, string | null>>;
}

export interface ShapeLevel {
  attr: string;
  weight: number;
}

export interface BrokenBlock {
  /** Block path, e.g. "Tide › Pods". */
  block: string;
  /** The level the block belongs to: 0 is the top of the hierarchy. */
  depth: number;
  pieces: number;
  /** Times a shopper walking one shelf meets the block again after leaving it, summed over shelves. */
  extraRuns: number;
  /** Share of the block's width in its largest piece. */
  largestShare: number;
  /** How much of its bounding rectangle the largest piece fills. */
  fill: number;
  /** The same with each row read from its first pack to its last: the outline alone, so air
   *  between packs (graded as fullness) leaves an L or a Z, not a gappy rectangle. */
  shapeFill: number;
  footprintCm: number;
  /** 1 − damage, floored at 0. */
  quality: number;
  /** What this block costs: break units × pieceCost, plus its shortfall from a full rectangle. */
  damage: number;
}

export interface BlockShapeOptions {
  /** Damage per break, a break being scaled by the share of the block it displaces. A second run
   *  along a shelf is `interleaveFactor` breaks: the shopper walking that shelf meets the block twice. */
  pieceCost: number;
  interleaveFactor: number;
  /** Quality kept by a piece that fills none of its rectangle; a full rectangle keeps all of it. */
  fillFloor: number;
  /** Packs each row of a block may fall short of its rectangle without cost: blocks end on
   *  pack boundaries, so an edge one pack out is how a tidy block looks. */
  jitterPacks: number;
  /** A block with no value at some level of its path parts its neighbours but is not graded.
   *  The column path clears its placeholders to no value; Studio grades what its items carry. */
  skipUnvalued?: boolean;
}

/** A wall's shelf rows: the boards each row really has (a row missing from a bay has a gap), and
 *  the stretches of them a product can stand on (a pole's strip is not a hole in a block). */
export interface ShapeWall {
  run: number;
  widthCm: number;
  rows: readonly {
    rank: number;
    spans: readonly { from: number; to: number }[];
    live?: readonly { from: number; to: number }[];
  }[];
}

export interface BlockShapeResult {
  graded: boolean;
  score: number;
  levels: readonly { attr: string; score: number; blocks: number; broken: number }[];
  worst: readonly BrokenBlock[];
  /** Every block that is in pieces, met twice along a shelf, or short of its rectangle. */
  broken: readonly BrokenBlock[];
  /** `broken`, worst first: `worst` is its first three. */
  ranked: readonly BrokenBlock[];
}

const pathKey = (stretch: ShapeStretch, attrs: readonly string[]) =>
  attrs.map((attr) => stretch.attrs[attr] ?? "—").join(PATH_SEPARATOR);

/** Two facings on neighbouring shelves are one block when they overlap by a quarter pack. */
const OVERLAP_SHARE = 0.25;

const unionFind = (size: number) => {
  const parent = Array.from({ length: size }, (_, index) => index);
  const find = (index: number): number =>
    parent[index] === index ? index : (parent[index] = find(parent[index]));
  return {
    find,
    union: (a: number, b: number) => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    },
  };
};

/**
 * The shelf below a facing, and how much of a row a rectangle really covers, read off the wall.
 * Bays whose shelves sit at different heights give the wall more rows than any one bay has: the
 * shelf under a facing in bay 1 may be three rows down, because the rows between belong to the
 * bay next door. Without a wall, rows are taken as consecutive and full width.
 */
const wallReader = (walls: readonly ShapeWall[] | undefined) => {
  const byRun = new Map(
    (walls ?? []).map((wall) => [wall.run, [...wall.rows].sort((a, b) => a.rank - b.rank)]),
  );
  const widthOf = new Map((walls ?? []).map((wall) => [wall.run, wall.widthCm]));
  const covers = (spans: readonly { from: number; to: number }[], x: number) =>
    spans.some((span) => x >= span.from - 1e-6 && x <= span.to + 1e-6);
  return {
    rowBelow: (run: number, rank: number, x: number): number | null => {
      const rows = byRun.get(run);
      if (!rows) return rank + 1;
      /* A board whose strip at x is dead (a rack's host shelf across the rack, an obstruction)
         is not under the facing there: the block continues on the next row that is live at x. */
      const next = rows.find((row) => row.rank > rank && covers(row.live ?? row.spans, x));
      return next ? next.rank : null;
    },
    /** The width of [from, to] that no row between `upper` and `lower` stands across. */
    clearWidth: (run: number, upper: number, lower: number, from: number, to: number): number => {
      if (to <= from) return 0;
      const blocking = (byRun.get(run) ?? [])
        .filter((row) => row.rank > upper && row.rank < lower)
        .flatMap((row) => row.live ?? row.spans)
        .map((span) => ({ from: Math.max(from, span.from), to: Math.min(to, span.to) }))
        .filter((span) => span.to > span.from)
        .sort((a, b) => a.from - b.from);
      let covered = 0;
      let reach = from;
      for (const span of blocking) {
        covered += Math.max(0, span.to - Math.max(reach, span.from));
        reach = Math.max(reach, span.to);
      }
      return to - from - covered;
    },
    widthIn: (run: number, rank: number, from: number, to: number): number => {
      const row = byRun.get(run)?.find((entry) => entry.rank === rank);
      if (!row) return byRun.has(run) ? 0 : to - from;
      const onShelves = (row.live ?? row.spans).reduce(
        (sum, span) => sum + Math.max(0, Math.min(to, span.to) - Math.max(from, span.from)),
        0,
      );
      // Product hanging off either end of the wall is still part of the block's rectangle.
      const wallWidth = widthOf.get(run) ?? Infinity;
      return onShelves + Math.max(0, -from) + Math.max(0, to - wallWidth);
    },
    /* Which of a row's spans is nearest x, as wallRows.boardIndexAt reads it: facings on two
       spans stand either side of a bay with no board on that row. */
    spanAt: (run: number, rank: number, x: number): number => {
      const spans = byRun.get(run)?.find((entry) => entry.rank === rank)?.spans ?? [];
      const gap = (span: { from: number; to: number }) => Math.max(span.from - x, x - span.to, 0);
      let nearest = 0;
      spans.forEach((span, index) => {
        if (gap(span) < gap(spans[nearest])) nearest = index;
      });
      return nearest;
    },
  };
};

export const scoreBlockShape = (
  stretches: readonly ShapeStretch[],
  levels: readonly ShapeLevel[],
  options: BlockShapeOptions,
  walls?: readonly ShapeWall[],
): BlockShapeResult => {
  if (levels.length === 0 || stretches.length === 0) {
    return { graded: false, score: 1, levels: [], worst: [], broken: [], ranked: [] };
  }
  const wall = wallReader(walls);

  const lanes = new Map<string, number[]>();
  stretches.forEach((stretch, index) => {
    const span = wall.spanAt(stretch.run, stretch.shelf, stretch.x + stretch.width / 2);
    const key = `${stretch.run}|${stretch.shelf}|${span}`;
    const lane = lanes.get(key);
    if (lane) lane.push(index);
    else lanes.set(key, [index]);
  });
  lanes.forEach((lane) => lane.sort((a, b) => stretches[a].x - stretches[b].x || a - b));

  const perLevel: { attr: string; score: number; blocks: number; broken: number }[] = [];
  const broken: BrokenBlock[] = [];
  /* Blocking is one minus the average damage per block, weighted by level. Damage is not
     capped per block, so a brand in pieces can cost more than its share and a scattered wall
     reaches zero, while a wall with a blemish or two stays high. */
  let damage = 0;
  let exposure = 0;

  levels.forEach((level, depth) => {
    const attrs = levels.slice(0, depth + 1).map((entry) => entry.attr);
    const keyOf = stretches.map((stretch) => `${stretch.run}␟${pathKey(stretch, attrs)}`);
    const sets = unionFind(stretches.length);

    // Along a shelf: neighbours in reading order with nothing foreign between them.
    lanes.forEach((lane) => {
      for (let i = 1; i < lane.length; i++) {
        if (keyOf[lane[i]] === keyOf[lane[i - 1]]) sets.union(lane[i], lane[i - 1]);
      }
    });

    // Across shelves: the same block on the shelf below, overlapping in x.
    const members = new Map<string, number[]>();
    keyOf.forEach((key, index) => {
      const list = members.get(key);
      if (list) list.push(index);
      else members.set(key, [index]);
    });
    members.forEach((list) => {
      const byShelf = new Map<number, number[]>();
      for (const index of list) {
        const shelf = stretches[index].shelf;
        const onShelf = byShelf.get(shelf);
        if (onShelf) onShelf.push(index);
        else byShelf.set(shelf, [index]);
      }
      byShelf.forEach((upper) => {
        for (const a of upper) {
          const sa = stretches[a];
          // A wide facing can sit over two bays whose next shelves differ; look under both ends.
          const inset = Math.min(sa.width / 2, OVERLAP_SHARE * sa.packWidth);
          const below = new Set(
            [sa.x + inset, sa.x + sa.width / 2, sa.x + sa.width - inset]
              .map((x) => wall.rowBelow(sa.run, sa.shelf, x))
              .filter((rank): rank is number => rank !== null),
          );
          below.forEach((rank) => {
            for (const b of byShelf.get(rank) ?? []) {
              const sb = stretches[b];
              const overlap = wall.clearWidth(
                sa.run,
                sa.shelf,
                rank,
                Math.max(sa.x, sb.x),
                Math.min(sa.x + sa.width, sb.x + sb.width),
              );
              if (overlap > OVERLAP_SHARE * Math.min(sa.packWidth, sb.packWidth)) sets.union(a, b);
            }
          });
        }
      });
    });

    /* Runs along a shelf: a block's facings on one shelf that are neighbours in reading order,
       with the width each run carries, block by block and shelf by shelf. */
    const runsOf = new Map<string, Map<number, number[]>>();
    lanes.forEach((lane) => {
      for (let i = 0; i < lane.length; i++) {
        const key = keyOf[lane[i]];
        const stretch = stretches[lane[i]];
        const byShelf = runsOf.get(key) ?? new Map<number, number[]>();
        const runs = byShelf.get(stretch.shelf) ?? [];
        if (i > 0 && keyOf[lane[i - 1]] === key) runs[runs.length - 1] += stretch.width;
        else runs.push(stretch.width);
        byShelf.set(stretch.shelf, runs);
        runsOf.set(key, byShelf);
      }
    });

    let levelDamage = 0;
    let levelBlocks = 0;
    let brokenHere = 0;
    members.forEach((list, key) => {
      // No value names no block.
      if (options.skipUnvalued && attrs.some((attr) => stretches[list[0]].attrs[attr] == null)) {
        return;
      }
      const runsByShelf = runsOf.get(key) ?? new Map<number, number[]>();
      let extraRuns = 0;
      runsByShelf.forEach((runs) => (extraRuns += runs.length - 1));
      const pieces = new Map<
        number,
        {
          mass: number;
          xMin: number;
          xMax: number;
          shelfMin: number;
          shelfMax: number;
          widestPack: number;
          rowMass: Map<number, number>;
          rowSpan: Map<number, { from: number; to: number }>;
        }
      >();
      let mass = 0;
      for (const index of list) {
        const stretch = stretches[index];
        const root = sets.find(index);
        const piece = pieces.get(root) ?? {
          mass: 0,
          xMin: Infinity,
          xMax: -Infinity,
          shelfMin: Infinity,
          shelfMax: -Infinity,
          widestPack: 0,
          rowMass: new Map<number, number>(),
          rowSpan: new Map<number, { from: number; to: number }>(),
        };
        piece.mass += stretch.width;
        piece.rowMass.set(stretch.shelf, (piece.rowMass.get(stretch.shelf) ?? 0) + stretch.width);
        const span = piece.rowSpan.get(stretch.shelf);
        piece.rowSpan.set(stretch.shelf, {
          from: Math.min(span?.from ?? Infinity, stretch.x),
          to: Math.max(span?.to ?? -Infinity, stretch.x + stretch.width),
        });
        piece.widestPack = Math.max(piece.widestPack, stretch.packWidth);
        piece.xMin = Math.min(piece.xMin, stretch.x);
        piece.xMax = Math.max(piece.xMax, stretch.x + stretch.width);
        piece.shelfMin = Math.min(piece.shelfMin, stretch.shelf);
        piece.shelfMax = Math.max(piece.shelfMax, stretch.shelf);
        pieces.set(root, piece);
        mass += stretch.width;
      }
      if (mass <= 0) return;
      const largest = [...pieces.values()].reduce((best, piece) =>
        piece.mass > best.mass ? piece : best,
      );
      /* Each row may fall short of the rectangle by up to one pack on its own; the allowance is
         not pooled, so one row two packs short is not paid for by a full row beside it. */
      const run = stretches[list[0]].run;
      let box = 0;
      let outlineBox = 0;
      let outline = 0;
      for (let shelf = largest.shelfMin; shelf <= largest.shelfMax; shelf++) {
        // Only the width a row really has under the rectangle: a bay with no shelf at this
        // height is not a hole in the block.
        const available = wall.widthIn(run, shelf, largest.xMin, largest.xMax);
        if (available <= 0) continue;
        const allowance = options.jitterPacks * largest.widestPack;
        const shortfall = Math.max(0, available - (largest.rowMass.get(shelf) ?? 0));
        box += available - Math.min(shortfall, allowance);
        const span = largest.rowSpan.get(shelf);
        // Live width only, as `available` is: a dead strip inside the outline holds no pack.
        const spanned = span ? Math.min(available, wall.widthIn(run, shelf, span.from, span.to)) : 0;
        outline += spanned;
        outlineBox += available - Math.min(available - spanned, allowance);
      }
      const fill = box > 0 ? Math.min(1, largest.mass / box) : 1;
      const shapeFill = outlineBox > 0 ? Math.min(1, outline / outlineBox) : 1;
      const share = largest.mass / mass;
      /* A break costs by how much of the block it displaces, softened by a square root: two
         facings of a sub-brand inside a fifty-facing stripe are a blemish, a brand torn in half
         is a wreck, and a single stray facing still shows. A second piece costs one break; a
         second run along a shelf costs `interleaveFactor` breaks, per shelf it happens on. */
      const pieceBreak = pieces.size > 1 ? Math.sqrt(1 - share) * Math.sqrt(pieces.size - 1) : 0;
      let runBreak = 0;
      runsByShelf.forEach((runs) => {
        if (runs.length < 2) return;
        const onShelf = runs.reduce((sum, run) => sum + run, 0);
        const displaced = (onShelf - Math.max(...runs)) / mass;
        runBreak += Math.sqrt(displaced) * Math.sqrt(runs.length - 1);
      });
      const blockDamage =
        options.pieceCost * (pieceBreak + options.interleaveFactor * runBreak) +
        (1 - options.fillFloor) * (1 - fill);
      const quality = Math.max(0, 1 - blockDamage);
      levelDamage += blockDamage;
      levelBlocks += 1;
      damage += blockDamage * level.weight;
      exposure += level.weight;
      if (pieces.size > 1 || extraRuns > 0 || Math.min(fill, shapeFill) < 0.999) {
        if (pieces.size > 1 || extraRuns > 0) brokenHere += 1;
        broken.push({
          block: key.split("␟").slice(1).join("␟"),
          depth,
          pieces: pieces.size,
          extraRuns,
          largestShare: +share.toFixed(3),
          fill: +fill.toFixed(3),
          shapeFill: +shapeFill.toFixed(3),
          footprintCm: +mass.toFixed(1),
          quality: +quality.toFixed(3),
          damage: +blockDamage.toFixed(3),
        });
      }
    });

    const levelScore = levelBlocks > 0 ? Math.max(0, 1 - levelDamage / levelBlocks) : 1;
    perLevel.push({
      attr: level.attr,
      score: +levelScore.toFixed(3),
      blocks: members.size,
      broken: brokenHere,
    });
  });

  const ranked = [...broken].sort(
    (a, b) => b.damage - a.damage || b.footprintCm - a.footprintCm || (a.block < b.block ? -1 : 1),
  );

  return {
    graded: exposure > 0,
    score: exposure > 0 ? Math.max(0, 1 - damage / exposure) : 1,
    levels: perLevel,
    worst: ranked.slice(0, 3),
    broken,
    ranked,
  };
};

/** E|ascending − descending| for `pairs` coin flips: the agreement a random order gets by luck. */
const chanceAgreement = (pairs: number): number => {
  if (pairs > 60) return Math.sqrt((2 * pairs) / Math.PI);
  let expected = 0;
  let coefficient = 1; // C(pairs, k)
  for (let k = 0; k <= pairs; k++) {
    if (k > 0) coefficient = (coefficient * (pairs - k + 1)) / k;
    expected += coefficient * Math.abs(2 * k - pairs);
  }
  return expected / 2 ** pairs;
};

/**
 * How orderly a set of blocks reads, beyond what chance gives. Each block offers |a − d| pairs of
 * agreement; a random order already offers E|a − d|, so only the excess counts, and a block
 * counts by the evidence it can give (pairs − chance). A block with one pair agrees with itself
 * every time and so proves nothing: without this, a shuffle that leaves blocks one pair long
 * looked more orderly than the plan it wrecked.
 */
export const orderlinessBeyondChance = (
  votes: Iterable<{ ascending: number; descending: number; weight?: number }>,
): { score: number; evidence: number } => {
  let excess = 0;
  let evidence = 0;
  for (const vote of votes) {
    const pairs = vote.ascending + vote.descending;
    if (pairs === 0) continue;
    const chance = chanceAgreement(pairs);
    const weight = vote.weight ?? 1;
    excess += Math.max(0, Math.abs(vote.ascending - vote.descending) - chance) * weight;
    evidence += (pairs - chance) * weight;
  }
  return { score: evidence > 0 ? Math.min(1, excess / evidence) : 1, evidence };
};

export interface BlockOrderResult {
  graded: boolean;
  score: number;
  pairs: number;
  /** The block whose children read least in order, when one does. */
  worst: { block: string; orderliness: number; pairs: number } | null;
}

/**
 * Inside a block, the children should read in the order the hierarchy ranks them.
 *
 * For each block at each level, neighbours along a shelf that belong to different children are
 * compared on the child level's priority. Either direction counts: a wall read right to left is
 * as orderly as one read left to right, and a mirrored plan must not be marked down here. A
 * shuffle is: its pairs split evenly both ways. Per block, orderliness = |ascending − descending|
 * / pairs; blocks are combined by their pair counts.
 *
 * `childAttrs[d]` is the attribute whose priority orders the children of a level-d block, which
 * is the next graded level, or the leaf column below the last one.
 */
export const scoreBlockOrder = (
  stretches: readonly ShapeStretch[],
  levels: readonly ShapeLevel[],
  childAttrs: readonly string[],
  priorityOf: (stretch: ShapeStretch, attr: string) => number | undefined,
): BlockOrderResult => {
  const lanes = new Map<string, ShapeStretch[]>();
  for (const stretch of stretches) {
    const key = `${stretch.run}|${stretch.shelf}`;
    const lane = lanes.get(key);
    if (lane) lane.push(stretch);
    else lanes.set(key, [stretch]);
  }
  lanes.forEach((lane) => lane.sort((a, b) => a.x - b.x));

  const tally = new Map<string, { ascending: number; descending: number; weight: number }>();
  levels.forEach((level, depth) => {
    const childAttr = childAttrs[depth];
    if (!childAttr) return;
    const attrs = levels.slice(0, depth + 1).map((entry) => entry.attr);
    lanes.forEach((lane) => {
      for (let i = 1; i < lane.length; i++) {
        const a = lane[i - 1];
        const b = lane[i];
        const block = pathKey(a, attrs);
        if (block !== pathKey(b, attrs)) continue;
        if ((a.attrs[childAttr] ?? "—") === (b.attrs[childAttr] ?? "—")) continue;
        const pa = priorityOf(a, childAttr);
        const pb = priorityOf(b, childAttr);
        if (pa === undefined || pb === undefined || pa === pb) continue;
        const key = `${a.run}␟${block}`;
        const entry = tally.get(key) ?? { ascending: 0, descending: 0, weight: level.weight };
        if (pa < pb) entry.ascending++;
        else entry.descending++;
        tally.set(key, entry);
      }
    });
  });

  let pairs = 0;
  let worst: BlockOrderResult["worst"] = null;
  tally.forEach((entry, key) => {
    const count = entry.ascending + entry.descending;
    pairs += count;
    const own = orderlinessBeyondChance([entry]);
    if (own.evidence > 0 && (!worst || own.score < worst.orderliness)) {
      worst = {
        block: key.split("␟").slice(1).join("␟"),
        orderliness: +own.score.toFixed(3),
        pairs: count,
      };
    }
  });
  const overall = orderlinessBeyondChance(tally.values());

  return { graded: overall.evidence > 0, score: overall.score, pairs, worst };
};
